import { Component, computed, effect, inject, OnInit, signal, ChangeDetectionStrategy } from '@angular/core'
import { BaseListing } from '../_base-listing/base-listing'
import { CoreService } from '../../services'
import { depotsColumns } from '../../utils/definitions.util'
import { injectInfiniteQuery, injectQuery } from '@tanstack/angular-query-experimental'
import { DepotService } from '../../services/entity-services/depot.service'

const DEFAULT_FILTERS = {
  name: '',
  user: null,
}

@Component({
  selector: 'app-depots',
  templateUrl: './depots.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class DepotsComponent extends BaseListing implements OnInit {
  private depotService = inject(DepotService)

  listingColumns = signal(depotsColumns)

  sortProp = signal('name')
  sortDir = signal('asc')

  filters = signal(structuredClone(DEFAULT_FILTERS))

  canCreate = computed(() => {
    return (
      this.core.services.user.hasRole('ROLE_ORDER_CREATE') ||
      this.core.services.user.hasRole('ROLE_SUPER_ADMIN') ||
      this.core.services.user.hasRole('ROLE_ADMIN')
    )
  })

  readonly queryFilters = computed(() => {
    const value = this.filters()
    const f: Record<string, any> = {}
    if (value.name?.length) f['name'] = value.name
    if (value.user) f['user'] = value.user
    return f
  })

  readonly queryDepots = injectInfiniteQuery(() => ({
    // queryKey: ['depots', this.sortProp(), this.sortDir(), this.filtersId()],
    queryKey: ['depots', this.sortProp(), this.sortDir()],
    queryFn: async ({ pageParam }) => {
      if (this.core.services.user.hasRole('ROLE_SALESMAN') && !this.core.services.user.user?.depots?.length) {
        return { items: [], paginator: { current: 1, endPage: 1, totalCount: 0 } } as any
      }
      return await this.depotService
        .getDepots(
          {
            page: pageParam,
            sort: this.sortProp(),
            direction: this.sortDir(),
            filters: this.queryFilters(),
          },
          { id: null, name: null, enabled: null, unavailablesUrl: null }
        )
        .then(result => {
          this.saveViewSettings('depots', {
            filters: this.filters(),
            sortProp: this.sortProp(),
            sortDir: this.sortDir(),
          })
          return result
        })
    },
    initialPageParam: 1,
    getNextPageParam: result =>
      Number(result?.paginator?.current) < Number(result?.paginator?.endPage)
        ? Number(result?.paginator?.current) + 1
        : undefined,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    placeholderData: prev => prev,
  }))

  totalItems = computed(() => this.queryDepots.data()?.pages?.[0]?.paginator?.totalCount ?? 0)

  constructor(protected core: CoreService) {
    super(core)
    effect(() => {
      this.queryDepots.data()
    })
  }

  async ngOnInit() {
    const view = this.loadViewSettings('depots')
    this.filters.set(view?.filters || structuredClone(DEFAULT_FILTERS))
    this.sortProp.set(view?.sortProp || 'name')
    this.sortDir.set(view?.sortDir || 'asc')

    if (this.core.services.user.hasRole('ROLE_SALESMAN') && !this.filters().user) {
      this.filters.update(v => ({ ...v, user: this.core.services.user.user?.id ?? null }))
    }

    this.loadSorting('depots')
    // only refetch if nothing cached
    const hasPages = this.queryDepots.data()?.pages?.length > 0
    if (!hasPages && !this.queryDepots.isFetching()) {
      await this.queryDepots.refetch()
    }
  }

  clearFilters() {
    this.filters.set(structuredClone(DEFAULT_FILTERS))
    if (this.core.services.user.hasRole('ROLE_SALESMAN')) {
      this.filters.update(v => ({ ...v, user: this.core.services.user.user?.id ?? null }))
    }
    this.queryDepots.refetch()
  }

  setFilters(property: string, value: any, dateLeftRight?: string) {
    if (dateLeftRight) {
      this.filters.set({
        ...this.filters(),
        [property]: {
          ...this.filters()[property],
          [dateLeftRight]: value,
        },
      })
    } else {
      this.filters.set({
        ...this.filters(),
        [property]: value,
      })
    }
  }
}
